/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 *
 * User Store
 */
import { configure, observable, decorate, action, computed, reaction } from 'mobx';
import { getUserInitials } from 'utils';
import { firebase } from 'containers/services/Firebase';
configure({ enforceActions: 'observed' });

class UserStore {
  isLoading = true;
  isLoadingProfile = false;
  isLoadingPreferences = false;
  isSaving = false;
  user = {};
  profile = {};
  preferences = {};
  hasUserChange = false;
  hasProfileChange = false;
  hasPreferencesChange = false;

  constructor(stores) {
    this._stores = stores;

    /**
     * Internal store listener
     */
    this._stores.appStore.debug(`UserStore [listener] Waiting to init user store...`);
    reaction(
      () => this._stores.authStore.isAuth,
      isAuth => {
        this._stores.appStore.debug(`UserStore [reaction] Detected a change in user auth status.`);
        if (isAuth) {
          this.initStore();
        } else {
          this._stores.appStore.debug(`UserStore [reaction] Detected a user signout event, resetting store.`);
          // Remove any listeners
          if (this.user.uid) {
            firebase.data.ref(`users/${this.user.uid}/claims`).off();
            firebase.data.ref(`users/${this.user.uid}/ProfileInfo`).off();
          }
          // Reset store
          this.reset();
        }
      }
    );

    // Bind methods to (this) if used inside a promise
    this.setField = this.setField.bind(this);
    this.setArrayField = this.setArrayField.bind(this);
  }

  /**
   * Internal stores getter.  Note: NOT mobx computed
   */
  get stores() {
    return this._stores;
  }

  /**
   * Mobx Computed
   * Returns the current authenticated user's uid.
   */
  get uid() {
    return this.stores.authStore.currentUser.uid || '';
  }

  /**
   * Mobx Computed
   * Returns true if any user observables change value
   */
  get hasChanges() {
    return this.hasUserChange || this.hasProfileChange || this.hasPreferencesChange;
  }

  /**
   * Generic action to set value of any store field.
   * @param {string} fieldName Name of store field to set.
   * @param {any} value Value of store field to set.
   * @returns {void}
   */
  setField(fieldName, value) {
    if (this[fieldName] !== undefined) {
      this[fieldName] = value;
    }
  }

  /**
   * Generic action to set value of the profile.address fields.
   * @param {string} fieldName Name of address field to set.
   * @param {any} value Value of address field to set.
   * @returns {void}
   */
  setAddressField(fieldName, value) {
    if (this.profile.address[fieldName] !== undefined) {
      this.profile.address[fieldName] = value;
    }
  }

  /**
   * Generic action to replace store array index value.
   * @param {array} array Store array to update
   * @param {int} index Array index to replace.
   * @param {any} newValue New array value.
   * @returns {void}
   */
  setArrayField(array, index, newValue) {
    array[index] = newValue;
  }

  /**
   * Syncs User Profile with Store
   */
  syncUserProfile = () => {
    this.setField('isLoadingProfile', true);
    firebase.data.ref(`users/${this.uid}/ProfileInfo`).on(
      'value',
      snapshot => {
        if (snapshot.exists()) {
          this.setField('profile', snapshot.val());
        }
        this.setField('isLoadingProfile', false);
      },
      error => {
        if (error) {
          this.setField('isLoadingProfile', false);
          this.stores.appStore.error('system', error);
        }
      }
    );
  };

  /**
   * Updates User Preferences
   */
  fetchUserPreferences = () => {
    this.setField('isLoadingPreferences', true);
    firebase.data
      .ref(`users/${this.uid}/preferences`)
      .once('value', snapshot => {
        if (snapshot.exists()) {
          this.setField('profile', snapshot.val());
        }
      })
      .then(() => {
        this.setField('isLoadingPreferences', false);
      })
      .catch(error => {
        this.setField('isLoadingPreferences', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Initializes store and subscribes to changes in user record.
   */
  initStore = () => {
    // Retrieve user record from the database
    this.setField('isLoading', true);
    firebase.data.ref(`users/${this.uid}/ProfileInfo`).on(
      'value',
      snapshot => {
        if (snapshot.exists()) {
          const snap = snapshot.val();
          const user = {
            uid: snap.uid,
            firstName: snap.firstName || '',
            lastName: snap.lastName || '',
            displayName: snap.displayName || '',
            initials: snap.initials || getUserInitials(snap.firstName, snap.lastName),
            email: snap.email || '',
            avatarUrl: snap.photoUrl || '',
            lastOnlineDtTm: snap.lastOnlineDtTm || new Date().toISOString(),
            type: snap.type || '',
          };

          // Update user
          this.setField('user', user);
          // Start user profile sync
          this.syncUserProfile();
          // Update user preferences
          this.fetchUserPreferences();
        } else {
          // User record does not exist
        }
        this.setField('isLoading', false);
      },
      error => {
        if (error) {
          this.stores.appStore.error('system', error);
        }
        this.setField('isLoading', false);
      }
    );
  };

  /**
   * Updates User Values
   * @param {event} e HTML Form Event
   * @returns {void}
   */
  handleInputChange = e => {
    this.user[e.target.id] = e.target.value;
    this.hasUserChange = true;
  };

  /**
   * Updates User Profile Values
   * @param {event} e HTML Form Event
   * @returns {void}
   */
  handleProfileInputChange = e => {
    this.profile[e.target.id] = e.target.value;
    this.hasProfileChange = true;
  };

  /**
   * Updates User Profile Address Values
   * @param {event} e HTML Form Event
   * @returns {void}
   */
  handleAddressInputChange = e => {
    this.profile.address[e.target.name] = e.target.value;
    this.hasProfileChange = true;
  };

  /**
   * Updates current users avatar image
   * @param {blob} file Uploaded file
   * @param {any} response Api response object
   */
  handleAvatarChange = (file, response) => {
    this.stores.appStore.debug('UserStore.handleAvatarChange() [action] Updating user avatar...');
    const avatarFile = file;
    const avatarUrl = response.secure_url;
    const store = this; // Binds store to this insite of async method.

    // Check to see if user uploaded a profile photo
    if (avatarUrl !== '') {
      const { format, height, width, signature } = response;
      const { lastModified, name, type, size } = avatarFile;
      try {
        const metadata = {
          contentType: 'image/' + format,
          height: height,
          width: width,
          signature: signature,
          lastModified: lastModified,
          fileName: name,
          fileType: type,
          fileSize: size,
        };

        // Fetch data from temp storage and upload to Firebase Storage

        var xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function(event) {
          store.stores.appStore.debug('UserStore.handleAvatarChange() [action] XMLHttpRequest.onload');
          var blob = xhr.response;
          store.stores.appStore.debug(
            'UserStore.handleAvatarChange() [action] Uploading new profile image to storage.'
          );

          const storageRef = firebase.storage.ref('app/profileImages');
          var uploadTask = storageRef
            .child(store.uid)
            .child('ProfilePicture.' + format)
            .put(blob, metadata);

          uploadTask.on(
            'state_changed',
            function(snapshot) {
              // Handle Progress Here if needed
              store.stores.appStore.debug('UserStore.handleAvatarChange() [action] Saving to storage...');
            },
            function(error) {
              store.stores.appStore.error('system', error);
            },
            function() {
              // Success, update download URL store reference
              uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
                store.stores.appStore.debug(
                  'UserStore.handleAvatarChange() [action] Save successful. New download url: ',
                  downloadURL
                );
                // Update User Account Profile Image URL
                // Update New User Profile Attributes
                store.stores.authStore.currentUser
                  .updateProfile({
                    photoURL: downloadURL,
                  })
                  .then(function() {
                    // User Profile updated successfully.
                  })
                  .catch(function(error) {
                    store.stores.appStore.error('system', error);
                  });

                // Update Database User ProfileInfo Picture URL
                // User Database Ref
                const userRef = firebase.data.ref(`users/${store.uid}/ProfileInfo`);

                userRef.update(
                  {
                    photoUrl: downloadURL,
                  },
                  function(error) {
                    if (error) {
                      store.stores.appStore.error('system', error);
                    } else {
                      // Success
                    }
                  }
                );
              });
            }
          );
        };

        xhr.open('GET', avatarUrl);
        xhr.send();
      } catch (error) {
        store.stores.appStore.error('system', error);
      }
    }
  };

  /**
   * User Profile Form Submission
   * Saves user store information to the database.
   * @param {event} e HTML Form Event
   * @returns {void}
   */
  onSubmit = e => {
    e.preventDefault();

    // Make sure store has values to save
    if (this.hasChanges) {
      this.setField('isSaving', true);
      // Add delay to provide a better user ui experience
      setTimeout(() => {
        firebase.data
          .ref('users')
          .child(this.uid)
          .update({
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            displayName: this.user.firstName + ' ' + this.user.lastName,
            ProfileInfo: {
              firstName: this.user.firstName,
              lastName: this.user.lastName,
              ...this.profile,
            },
            preferences: this.preferences || null,
          })
          .then(() => {
            // Success
            this.setField('hasUserChange', false);
            this.setField('hasProfileChange', false);
            this.setField('hasPreferencesChange', false);
            this.stores.portalStore.snackbar.showSnackbarWithVariantWithDuration(
              'Profile saved successfully.',
              'success',
              2750
            );
            this.setField('isSaving', false);
          })
          .catch(error => {
            this.setField('isSaving', false);
            this.stores.portalStore.loading.hidePageLoader();
          });
      }, 300);
    }
  };

  /**
   * Resets store values to default
   */
  reset() {
    this.isLoading = true;
    this.isLoadingProfile = false;
    this.isLoadingPreferences = false;
    this.isSaving = false;
    this.user = {};
    this.profile = {};
    this.preferences = {};
    this.hasUserChange = false;
    this.hasProfileChange = false;
    this.hasPreferencesChange = false;
    this.stores.appStore.debug('UserStore.reset() [action] User store has been reset.');
  }
}

decorate(UserStore, {
  isLoading: observable,
  isLoadingProfile: observable,
  isLoadingPreferences: observable,
  isSaving: observable,
  user: observable,
  profile: observable,
  preferences: observable,
  hasUserChange: observable,
  hasProfileChange: observable,
  hasPreferencesChange: observable,
  uid: computed,
  hasChanges: computed,
  setField: action,
  setAddressField: action,
  setArrayField: action,
  syncUserProfile: action,
  fetchUserPreferences: action,
  initStore: action,
  handleInputChange: action,
  handleProfileInputChange: action,
  handleAddressInputChange: action,
  handleAvatarChange: action,
  onSubmit: action,
  reset: action,
});

export default UserStore;
